import React, { useEffect } from "react";
import { images } from "@img/imagesMap";
import StyledDateInput from "@components/common/StyledDateInput";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@components/ui/tooltip.tsx";

interface WeeklyOffSettingsProps {
    weeklyOffSettings: {
        use_variable_cycle?: boolean;
        cycle_type?: "month" | "week";
        cycle_interval?: number;
        cycle_start_date?: string;
        shift_variation?: number;
    } | null;
    handleSettingsChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | {
        target: {
            name: string;
            value: string;
            type?: string;
            checked?: boolean;
        };
    }) => void;
    formatDate: (value: string) => string;
    getDateValue: (value: string | Date | undefined | null) => string;
    isValidDateString: (str: string) => boolean;
    weekOffset: number;
    setWeekOffset: (value: number) => void;
}

export default function WeeklyOffSettings({
    weeklyOffSettings,
    handleSettingsChange,
    formatDate,
    getDateValue,
    isValidDateString,
    weekOffset,
    setWeekOffset,
}: WeeklyOffSettingsProps) {
    const [isDropdownOpen, setIsDropdownOpen] = React.useState<boolean>(false);

    const weekOptions = React.useMemo(() => [-3, -2, -1, 0, 1, 2, 3], []);

    const getWeekLabel = React.useCallback((value: number): string => {
        if (value === 0) return "오늘";
        return value > 0 ? `+${value}` : `${value}`;
    }, []);

    const isInitialized = React.useRef(false);

    // 입력날짜 디폴트는 오늘 날짜
    useEffect(() => {
        if (weeklyOffSettings) {
            if (!isInitialized.current) {
                if (!weeklyOffSettings.cycle_start_date) {
                    const today = new Date();
                    const year = today.getFullYear();
                    const month = String(today.getMonth() + 1).padStart(2, '0');
                    const day = String(today.getDate()).padStart(2, '0');
                    const todayStr = `${year}-${month}-${day}`;

                    handleSettingsChange({
                        target: {
                            name: "cycle_start_date",
                            value: todayStr,
                            type: "text",
                        },
                    } as any);
                }
                isInitialized.current = true;
            }
        }
    }, [weeklyOffSettings, handleSettingsChange]);



    return (
        <div className="flex justify-center items-center gap-[20px] mb-6">
            {/* Auto/Manual Schedule Toggle */}
            <div className="flex flex-col gap-[19px]">
                <button
                    onClick={() =>
                        handleSettingsChange({
                            target: {
                                name: "use_variable_cycle",
                                type: "checkbox",
                                checked: true,
                            },
                        })
                    }
                    className={`flex justify-center items-center px-12 py-5 rounded-lg font-bold transition-colors min-w-[180px] font-pretendard ${weeklyOffSettings?.use_variable_cycle === true
                        ? "bg-[#0e59c6] text-white"
                        : "bg-white text-[#B8B8BF] border border-gray-300 hover:bg-gray-50"
                        }`}
                    disabled={!weeklyOffSettings}
                >
                    <span className="font-bold font-pretendard text-[20px]">
                        자동 순환
                    </span>
                </button>
                <button
                    onClick={() =>
                        handleSettingsChange({
                            target: {
                                name: "use_variable_cycle",
                                type: "checkbox",
                                checked: false,
                            },
                        })
                    }
                    className={`flex justify-center items-center px-12 py-5 rounded-[10px] font-bold transition-colors min-w-[180px] font-pretendard ${weeklyOffSettings?.use_variable_cycle === false
                        ? "bg-[#0e59c6] text-white"
                        : "bg-white text-[#B8B8BF] border border-gray-300 hover:bg-gray-50"
                        }`}
                    disabled={!weeklyOffSettings}
                >
                    <span className="font-bold font-pretendard text-[20px]">
                        수동 순환
                    </span>
                </button>
            </div>

            {/* Cycle Settings */}
            <div className={`flex-1 bg-white rounded-[10px] px-[30px] ${weeklyOffSettings?.use_variable_cycle === false ? 'py-[33px]' : 'py-5'
                } border border-gray-200 max-w-[370px] max-h-[142px]`}>
                <h3 className="text-[24px] font-bold text-[#0e59c6] mb-[14px] text-center font-pretendard">
                    순환주기 {weeklyOffSettings?.use_variable_cycle === false && '시작일'}
                </h3>
                <div className={`flex items-center gap-6 ${weeklyOffSettings?.use_variable_cycle === false && 'justify-center'
                    }`}>
                    <div className="flex flex-col">
                        {weeklyOffSettings?.use_variable_cycle && (
                            <div className="flex items-center justify-center mb-[13px]">
                                <div className="flex mr-3">
                                    <label className="flex items-center gap-2 cursor-pointer font-pretendard">
                                        <input
                                            type="radio"
                                            name="cycle_type"
                                            value="month"
                                            checked={weeklyOffSettings?.cycle_type === "month"}
                                            onChange={handleSettingsChange}
                                            className="w-4 h-4 text-[#0e59c6]"
                                        />
                                        <span className="text-[16px] text-[#333333] font-pretendard">
                                            월별제
                                        </span>
                                    </label>
                                </div>

                                <div className={`flex items-center gap-[6px] ml-4'
                                    }`}>

                                    <span className="text-[16px] text-[#333333] whitespace-nowrap font-pretendard">
                                        시작일
                                    </span>

                                    {/* 캘린더로 적용 */}
                                    <StyledDateInput
                                        value={getDateValue(weeklyOffSettings?.cycle_start_date)}
                                    onChange={(e) => {
                                            // const formatted = formatDate(e.target.value);

                                            // 입력 순간에는 형식만 잡고
                                            handleSettingsChange({
                                                target: {
                                                    name: "cycle_start_date",
                                                    value: e,
                                                    type: "text",
                                                },
                                            } as any);
                                        }}
                                        placeholder="연도-월-일"
                                    />
                                </div>
                            </div>

                        )}
                        <div className="flex items-center">
                            {/* 일단 주기제 비활성화 - 업데이트 예정임 */}
                            {weeklyOffSettings?.use_variable_cycle && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <label className="flex items-center gap-2 cursor-not-allowed opacity-50">
                                            <input
                                                type="radio"
                                                name="cycle_type"
                                                value="week"
                                                checked={weeklyOffSettings?.cycle_type === "week"}
                                                onChange={handleSettingsChange}
                                                className="w-4 h-4 text-[#0e59c6]"
                                                disabled
                                            />
                                            <span className="whitespace-nowrap text-[16px] text-[#333333] font-pretendard">
                                                주기제
                                            </span>
                                        </label>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-[#333333] opacity-80" arrowClassName="fill-[#333333]">
                                        <p>{`업데이트 예정`}</p>
                                    </TooltipContent>
                                </Tooltip>




                            )}
                            {/* TODO : 일단 주기제 비활성화로 주석처리 / 나중에 주석 풀면 됨 */}
                            {/* {weeklyOffSettings?.use_variable_cycle &&
                                weeklyOffSettings?.cycle_type === "week" && (
                                    <div className="ml-[6px] mr-[9px] flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-md max-w-[49px] max-h-[30px]">
                                        <input
                                            type="number"
                                            name="cycle_interval"
                                            value={weeklyOffSettings?.cycle_interval || ""}
                                            onChange={handleSettingsChange}
                                            className="w-8 text-center text-sm font-medium text-gray-800 bg-transparent border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        />
                                        <span className="text-[16px] text-gray-600">주</span>
                                    </div>
                                )} */}

                            {/* 수동 순환용 시작일 캘린더 */}
                            {weeklyOffSettings?.use_variable_cycle === false && <div className={`flex items-center gap-[6px] ml-4 ${weeklyOffSettings?.use_variable_cycle === false && '!ml-0 mt-[6px]'
                                }`}>
                                {/* 캘린더로 적용 */}
                                <StyledDateInput
                                    value={getDateValue(weeklyOffSettings?.cycle_start_date)}
                                    onChange={(e) => {
                                        // const formatted = formatDate(e.target.value);

                                        // 입력 순간에는 형식만 잡고
                                        handleSettingsChange({
                                            target: {
                                                name: "cycle_start_date",
                                                value: e,
                                                type: "text",
                                            },
                                        } as any);
                                    }}
                                    placeholder="연도-월-일"
                                />
                            </div>}

                        </div>
                    </div>
                </div>
            </div>

            {/* 주기 변동일 수 */}
            {weeklyOffSettings?.use_variable_cycle && (
                <div className="flex flex-col bg-white rounded-[10px] px-6 py-4 border border-gray-200 relative min-w-[220px] items-center">
                    <h3 className="text-[24px] font-bold text-[#0e59c6] mb-[14px] text-center font-pretendard">
                        주기 변동일 수
                    </h3>
                    <div className="relative flex items-center">
                        <span className="text-[16px] justify-center items-center text-center mr-[3px] font-pretendard mt-[3px]">
                            변동일
                        </span>
                        <button
                            type="button"
                            className="conf_selectType conf_selectType--button max-w-[67px] text-[15px]"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        >
                            <span className="text-[15px] font-pretendard">
                                {getWeekLabel(weeklyOffSettings?.shift_variation ?? 0)}
                            </span>
                        </button>

                        {/* Dropdown Menu */}
                        {isDropdownOpen && (
                            <>
                                {/* Backdrop */}
                                <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setIsDropdownOpen(false)}
                                />

                                {/* Dropdown */}
                                <div className="absolute top-full mt-1 right-0 w-32 bg-white border border-gray-300 rounded-[10px] shadow-lg z-20 max-w-[67px] font-pretendard">
                                    {weekOptions.map((option) => (
                                        <button
                                            key={option}
                                            onClick={() => {
                                                handleSettingsChange({
                                                    target: {
                                                        name: "shift_variation",
                                                        value: String(option),
                                                    },
                                                });
                                                setIsDropdownOpen(false);
                                            }}
                                            className={`w-full px-4 py-2 text-[15px] text-center border-[#939399] hover:bg-gray-100 transition-colors font-pretendard ${weeklyOffSettings?.shift_variation === option
                                                ? "bg-blue-50 text-blue-600 font-medium"
                                                : "text-gray-700"
                                                }`}
                                        >
                                            {getWeekLabel(option)}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Tooltip/Help Text */}
                    <div className="absolute top-0 right-0 mt-2 mr-2">
                        <div className="group relative">
                            <button className="text-gray-400 hover:text-gray-600">
                                <img src={images.icon_tooltip} alt="" className="" style={{ height: "1em" }} />
                            </button>
                            <div className="hidden group-hover:block absolute left-0 top-6 p-2 bg-[#939399] text-white text-[11px] rounded shadow-lg z-30 w-auto">
                                <div className="whitespace-nowrap mb-[3px]">
                                    순환 주기 경과 시 자동으로 주기 변동일 수에 따라 적용
                                </div>
                                <div className="whitespace-nowrap">
                                    (예: 4주/수요일 → 4주 후/화요일)
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-center items-center">
                        <p className="text-[14px] text-[#B8B8BF] mt-[15px] text-center font-pretendard">
                            {`예) 변동요일이 -1일 경우`}
                            <br />
                            {` 순환주기 후 수요일 ->화요일`}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}